import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { BrandMark, IconChevronLeft } from './Icons';
import { AccountMenu } from './AccountMenu';
import { ExamModeBanner } from './ExamModeBanner';

interface AppShellProps {
  children: ReactNode;
  /** Optional override to hide the top bar entirely on full-bleed flows. */
  hideTopBar?: boolean;
  /** Title shown in the top bar centre. Defaults to the brand. */
  title?: string;
  /** If true, show a back arrow on the left that goes to /app. */
  showBack?: boolean;
}

// Wraps every /app route with the standard chrome: top bar, padded content, bottom nav.
// Responsive: max-width 460 px on mobile, shadow + breathing room on tablet, restricted
// width on desktop so it never feels full-bleed on a laptop screen.
export function AppShell({ children, hideTopBar = false, title, showBack = false }: AppShellProps) {
  return (
    <div className="min-h-full w-full bg-cream/80 flex justify-center">
      <div className="w-full max-w-[460px] md:max-w-[520px] xl:max-w-[640px] min-h-full bg-cream md:shadow-2xl md:shadow-ink/5 md:my-4 md:rounded-3xl md:overflow-hidden flex flex-col">
        <ExamModeBanner />
        {!hideTopBar && (
          <header className="flex items-center justify-between px-4 py-3 bg-cream/90 backdrop-blur-md sticky top-0 z-20 border-b border-ink/5">
            <div className="flex items-center gap-2 min-w-0">
              {showBack ? (
                <Link
                  to="/app"
                  aria-label="Back to home"
                  className="w-9 h-9 rounded-full text-ink/60 hover:text-ink hover:bg-ink/5 grid place-items-center transition"
                >
                  <IconChevronLeft size={20} />
                </Link>
              ) : (
                <Link to="/app" aria-label="Home" className="flex items-center gap-2">
                  <BrandMark size={28} />
                </Link>
              )}
              <span className="font-medium text-ink text-sm tracking-tight truncate">
                {title ?? 'Answer Assist'}
              </span>
            </div>
            <AccountMenu />
          </header>
        )}

        <main className="flex-1 pb-24">{children}</main>
        {/* Bottom nav is rendered at the AppRouteWrapper level (in App.tsx) so that it
            persists across every /app screen, including the capture and answer flows
            that use the simpler Screen container. */}
      </div>
    </div>
  );
}
